import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { toast } from "sonner";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const PREVIEW = "https://static.prod-images.emergentagent.com/jobs/00f7dcdb-2369-426b-9b75-6a937da6bf82/images/265e9c921fc8388e96d5521f08048b5f46dfe1546baed81e98c7451b084a3719.png";
const SIZES = ["S", "M", "L", "XL", "2XL", "3XL"];
const FITS = ["Unisex", "Men's", "Women's"];

const perks = [
  "One exclusive KC-inspired tee every month",
  "Member-only designs you can't buy in the shop",
  "Pause, cancel, or change your size anytime",
  "Free stickers with your first drop",
  "Early access to limited collections",
];

const faqs = [
  { q: "How does the monthly Tee Club work?", a: "Each month we design and print a brand-new tee celebrating a piece of Kansas City culture. As a subscriber, it ships straight to your door — no need to reorder." },
  { q: "Can I change my size?", a: "Absolutely. Head to your account page anytime to update your size or fit. Changes apply to your next monthly shipment." },
  { q: "Can I pause or cancel?", a: "Yes. You can pause your subscription if you need a break, or cancel entirely — no penalties, no hassle. Manage it all from your account." },
  { q: "When am I billed?", a: "Subscriptions bill monthly on the date you signed up. (Payments are currently in mock mode for this demo.)" },
  { q: "What sizes and fits do you offer?", a: "Sizes S through 3XL in Unisex, Men's, and Women's fits. All tees are heavyweight, pre-shrunk cotton." },
];

const TeeClub = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [size, setSize] = useState("M");
  const [fit, setFit] = useState("Unisex");
  const [loading, setLoading] = useState(false);

  const subscribe = async () => {
    if (!user) {
      toast.error("Please sign in to join the Tee Club");
      return navigate("/login", { state: { from: { pathname: "/tee-club" } } });
    }
    setLoading(true);
    try {
      await api.post("/subscriptions", { size, fit, plan: "monthly" });
      toast.success("You're in the Tee Club! 🎉");
      navigate("/account");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not subscribe");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="tee-club-page">
      <section className="bg-[#1C1C1C] text-[#FDFBF7] grain relative overflow-hidden border-b-2 border-[#1C1C1C]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 grid md:grid-cols-2 gap-12 items-center relative">
          <div>
            <span className="inline-block bg-[#D35400] text-white text-xs font-bold uppercase tracking-[0.25em] px-3 py-1.5 mb-6">
              Monthly Subscription
            </span>
            <h1 className="font-display text-6xl md:text-8xl leading-[0.9]">The KC<br />Tee Club</h1>
            <p className="text-xl text-[#C9C4BA] mt-6 max-w-md leading-relaxed">
              A fresh Kansas City-inspired tee every month. Wear KC history, one drop at a time.
            </p>
            <div className="mt-8 flex items-baseline gap-2">
              <span className="font-display text-7xl text-[#D35400]">$24</span>
              <span className="text-lg text-[#C9C4BA]">/ month</span>
            </div>
          </div>
          <div className="border-2 border-[#D35400] bg-[#242424] p-3 rotate-2">
            <img src={PREVIEW} alt="Monthly tee preview" className="w-full aspect-square object-cover" />
          </div>
        </div>
      </section>

      {/* SIGN UP CARD */}
      <section className="max-w-3xl mx-auto px-6 py-16">
        <div className="border-2 border-[#1C1C1C] hard-shadow-lg bg-[#F5F2EA] p-8 md:p-10" data-testid="subscription-card">
          <h2 className="font-display text-4xl mb-6">Build Your Subscription</h2>

          <div className="mb-6">
            <h3 className="font-display text-2xl mb-3">Fit</h3>
            <div className="flex flex-wrap gap-2">
              {FITS.map((f) => (
                <button
                  key={f}
                  onClick={() => setFit(f)}
                  data-testid={`fit-${f.replace(/[^a-zA-Z]/g, "")}`}
                  className={`px-5 py-3 border-2 border-[#1C1C1C] font-bold transition-all ${fit === f ? "bg-[#1C1C1C] text-[#FDFBF7]" : "hover:bg-[#EDE8DD]"}`}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>

          <div className="mb-8">
            <h3 className="font-display text-2xl mb-3">Size</h3>
            <div className="flex flex-wrap gap-2">
              {SIZES.map((s) => (
                <button
                  key={s}
                  onClick={() => setSize(s)}
                  data-testid={`sub-size-${s}`}
                  className={`min-w-[56px] px-4 py-3 border-2 border-[#1C1C1C] font-bold transition-all ${size === s ? "bg-[#1C1C1C] text-[#FDFBF7]" : "hover:bg-[#EDE8DD]"}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          <ul className="space-y-2 mb-8">
            {perks.map((p) => (
              <li key={p} className="flex items-start gap-3 text-[#4A4A4A]">
                <Check className="w-5 h-5 text-[#D35400] shrink-0 mt-0.5" strokeWidth={3} />
                <span>{p}</span>
              </li>
            ))}
          </ul>

          <button onClick={subscribe} disabled={loading} className="btn-primary w-full px-8 py-5 text-lg disabled:opacity-60" data-testid="join-tee-club-button">
            {loading ? "Joining…" : "Join the Tee Club — $24/mo"}
          </button>
          <p className="text-center text-xs text-[#4A4A4A] mt-3">Mock checkout · Pause or cancel anytime from your account.</p>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-6 pb-24">
        <h2 className="font-display text-5xl md:text-6xl mb-8 text-center">Questions?</h2>
        <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
          {faqs.map((f, i) => (
            <AccordionItem key={f.q} value={`item-${i}`} className="border-2 border-[#1C1C1C] bg-[#F5F2EA] px-5">
              <AccordionTrigger className="font-display text-2xl tracking-wide hover:no-underline text-left" data-testid={`faq-${i}`}>
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-[#4A4A4A] text-base leading-relaxed pb-4">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>
    </div>
  );
};

export default TeeClub;
